import { useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { siteAnalytics } from "../lib/api.js";
import { useApi } from "../hooks/index.js";
import { Spinner, Empty } from "../components/ui.jsx";
import { useLang } from "../lib/i18n.jsx";
import { Download, Globe, Users, Activity, Clock, MousePointer } from "lucide-react";

const COLORS = ["#0db87e", "#f5a623", "#2563eb", "#e03050", "#8aaa98", "#7c3aed", "#06b6d4"];

const SOURCE_OPTS = [
  { value: "",       label: "All Sources" },
  { value: "portal", label: "Public Website" },
  { value: "admin",  label: "Admin Portal" },
  { value: "mobile", label: "Mobile App" },
];

const RANGE_PRESETS = [
  { label: "7d",  days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function fmtDuration(sec) {
  if (!sec) return "0s";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="stat-card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div className="sc-lbl">{label}</div>
        <Icon size={16} color={color ?? "var(--teal)"} />
      </div>
      <div className="sc-val">{value}</div>
      {sub && <div className="sc-sub">{sub}</div>}
    </div>
  );
}

export default function PageVisitorAnalytics() {
  const { t } = useLang();
  const [source, setSource] = useState("");
  const [days, setDays] = useState(30);
  const [exporting, setExporting] = useState(false);

  const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const to = new Date().toISOString().slice(0, 10);
  const params = { source: source || undefined, from, to };

  const { data: overview, loading: loadingOv, reload: reloadOv } = useApi(() => siteAnalytics.overview(params), [source, days]);
  const { data: geo,      loading: loadingGeo }    = useApi(() => siteAnalytics.geo(params), [source, days]);
  const { data: devices,  loading: loadingDev }    = useApi(() => siteAnalytics.devices(params), [source, days]);
  const { data: pages,    loading: loadingPages }  = useApi(() => siteAnalytics.pages(params), [source, days]);
  const { data: sources,  loading: loadingSrc }    = useApi(() => siteAnalytics.sources(params), [source, days]);
  const { data: trends,   loading: loadingTrends } = useApi(() => siteAnalytics.trends({ ...params, granularity: days > 60 ? "week" : "day" }), [source, days]);
  const { data: realtime, loading: loadingRt, reload: reloadRt } = useApi(() => siteAnalytics.realtime({ source: source || undefined }), [source]);

  const o   = overview ?? {};
  const trend = trends?.trend ?? [];
  const countries = geo?.countries ?? [];
  const cities = geo?.cities ?? [];
  const continents = geo?.continents ?? [];
  const browsers = devices?.browsers ?? [];
  const osList = devices?.os ?? [];
  const deviceTypes = devices?.deviceTypes ?? [];
  const topPages = pages?.pages ?? [];
  const trafficSources = sources?.sources ?? [];
  const liveSessions = realtime?.sessions ?? [];

  const handleExport = async () => {
    setExporting(true);
    try { await siteAnalytics.exportCsv(params); }
    catch (e) { alert(e.message ?? "Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <div>
      {/* Filters */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-body" style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select className="input" style={{ width: "auto" }} value={source} onChange={e => setSource(e.target.value)}>
              {SOURCE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div style={{ display: "flex", gap: 4, background: "var(--surface)", borderRadius: 8, padding: 3 }}>
              {RANGE_PRESETS.map(r => (
                <button key={r.label} className="btn btn-xs"
                  style={{
                    background: days === r.days ? "var(--teal)" : "transparent",
                    color: days === r.days ? "white" : "var(--muted)",
                    border: "none",
                  }}
                  onClick={() => setDays(r.days)}>
                  {r.label}
                </button>
              ))}
            </div>
            <button className="btn btn-out btn-xs" onClick={() => { reloadOv(); reloadRt(); }}>
              <Activity size={12} /> Refresh
            </button>
          </div>
          <button className="btn btn-out btn-xs" onClick={handleExport} disabled={exporting}>
            <Download size={12} /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </div>

      {/* Overview stat cards */}
      {loadingOv ? <Spinner /> : (
        <div className="stats-grid" style={{ marginBottom: 18 }}>
          <StatCard icon={Users}       label="Total Visitors"     value={(o.totalVisitors ?? 0).toLocaleString()} sub={`${o.uniqueVisitors ?? 0} logged-in`} />
          <StatCard icon={Activity}    label="Active Now"         value={o.activeNow ?? 0}      sub="last 5 min" color="#0db87e" />
          <StatCard icon={MousePointer} label="Page Views"        value={(o.pageViews ?? 0).toLocaleString()} sub={`${o.bounceRate ?? 0}% bounce rate`} color="#2563eb" />
          <StatCard icon={Clock}       label="Avg. Session"       value={fmtDuration(o.avgSessionDurationSec)} sub={`${o.returningVisitors ?? 0} returning`} color="#f5a623" />
        </div>
      )}

      {/* Trends chart */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-hdr">
          <div className="card-ttl"><div className="card-ico">📈</div>Visitor Trends</div>
          <span style={{ fontSize: 11, color: "var(--ghost)" }}>{from} → {to}</span>
        </div>
        <div className="card-body">
          {loadingTrends ? <Spinner /> : trend.length === 0 ? <Empty icon="📈" message="No data yet for this range" /> : (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={trend} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                <defs>
                  <linearGradient id="visGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0db87e" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#0db87e" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--ghost)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "var(--ghost)" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "var(--ink)", border: "none", borderRadius: 8 }} labelStyle={{ color: "white", fontWeight: 700 }} />
                <Area type="monotone" dataKey="visitors"  name="Visitors"   stroke="#0db87e" fill="url(#visGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="pageViews" name="Page Views" stroke="#2563eb" fill="url(#pvGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="cg2">
        {/* Geo: countries */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">🌍</div>Visitors by Country</div>
            <span style={{ fontSize: 11, color: "var(--ghost)" }}>{continents.length} continents</span>
          </div>
          <div className="card-body">
            {loadingGeo ? <Spinner /> : countries.length === 0 ? <Empty icon="🌍" message="No location data yet" /> : (
              <div className="tbl-wrap" style={{ maxHeight: 280, overflowY: "auto" }}>
                <table>
                  <thead><tr><th>Country</th><th>Visitors</th></tr></thead>
                  <tbody>
                    {countries.slice(0, 15).map((c, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{c.country}</td>
                        <td style={{ fontFamily: "var(--fm)" }}>{c.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Traffic sources */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">🔗</div>Traffic Sources</div>
          </div>
          <div className="card-body">
            {loadingSrc ? <Spinner /> : trafficSources.length === 0 ? <Empty icon="🔗" message="No data yet" /> : (
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <ResponsiveContainer width={160} height={160} minWidth={140}>
                  <PieChart>
                    <Pie data={trafficSources} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={3}>
                      {trafficSources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ flex: 1, minWidth: 100 }}>
                  {trafficSources.map((s, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>{s.source}</span>
                      <span style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top pages */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-hdr">
          <div className="card-ttl"><div className="card-ico">📄</div>Top Pages</div>
        </div>
        <div className="card-body">
          {loadingPages ? <Spinner /> : topPages.length === 0 ? <Empty icon="📄" message="No pageview data yet" /> : (
            <div className="tbl-wrap" style={{ maxHeight: 280, overflowY: "auto" }}>
              <table>
                <thead><tr><th>Path</th><th>Views</th></tr></thead>
                <tbody>
                  {topPages.map((p, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: "var(--fm)", fontSize: 12 }}>{p.path}</td>
                      <td style={{ fontFamily: "var(--fm)" }}>{p.views.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="cg2">
        {/* Devices / browsers */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">💻</div>Browsers & OS</div>
          </div>
          <div className="card-body">
            {loadingDev ? <Spinner /> : browsers.length === 0 ? <Empty icon="💻" message="No device data yet" /> : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={browsers.slice(0, 6)} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--ghost)" }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Tooltip contentStyle={{ background: "var(--ink)", border: "none", borderRadius: 8 }} labelStyle={{ color: "white" }} />
                  <Bar dataKey="count" name="Sessions" radius={[6, 6, 0, 0]}>
                    {browsers.slice(0, 6).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
            {osList.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {osList.slice(0, 6).map((os, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, background: "var(--surface)", border: "1px solid var(--border)" }}>
                    <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>{os.name}</span>
                    <span style={{ fontFamily: "var(--fm)", fontSize: 11, fontWeight: 700 }}>{os.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Device types */}
        <div className="card" style={{ marginBottom: 0 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">📱</div>Device Types</div>
          </div>
          <div className="card-body">
            {loadingDev ? <Spinner /> : deviceTypes.length === 0 ? <Empty icon="📱" message="No device data yet" /> : (
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <ResponsiveContainer width={160} height={160} minWidth={140}>
                  <PieChart>
                    <Pie data={deviceTypes} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={3}>
                      {deviceTypes.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ flex: 1, minWidth: 100 }}>
                  {deviceTypes.map((d, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                      <span style={{ fontSize: 12, fontWeight: 600, flex: 1, textTransform: "capitalize" }}>{d.name}</span>
                      <span style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cities */}
      {cities.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-hdr">
            <div className="card-ttl"><div className="card-ico">🏙️</div>Top Cities</div>
          </div>
          <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12 }}>
            {cities.slice(0, 12).map((c, i) => (
              <div key={i} style={{ textAlign: "center", padding: 12, background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)" }}>
                <div style={{ fontFamily: "var(--fd)", fontSize: 18, fontWeight: 900, color: COLORS[i % COLORS.length] }}>{c.count}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", marginTop: 2 }}>{c.city}</div>
                <div style={{ fontSize: 10, color: "var(--ghost)" }}>{c.country}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Real-time visitors */}
      <div className="card">
        <div className="card-hdr">
          <div className="card-ttl"><div className="card-ico">🟢</div>Active Now</div>
          <span style={{ fontSize: 11, color: "var(--ghost)" }}>{liveSessions.length} online (last 5 min)</span>
        </div>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr><th>Source</th><th>Location</th><th>Page</th><th>Browser / OS</th><th>User</th><th>Last Seen</th></tr>
            </thead>
            <tbody>
              {loadingRt ? (
                <tr><td colSpan={6}><Spinner /></td></tr>
              ) : liveSessions.length === 0 ? (
                <tr><td colSpan={6}><Empty icon="🟢" message="No active visitors right now" /></td></tr>
              ) : liveSessions.map((s, i) => (
                <tr key={i}>
                  <td><span className="badge bg-teal">{s.source}</span></td>
                  <td style={{ fontSize: 12 }}>{[s.city, s.country].filter(Boolean).join(", ") || "—"}</td>
                  <td style={{ fontFamily: "var(--fm)", fontSize: 11 }}>{s.exitPath ?? "—"}</td>
                  <td style={{ fontSize: 11, color: "var(--muted)" }}>{[s.browser, s.os].filter(Boolean).join(" / ") || "—"}</td>
                  <td style={{ fontSize: 12 }}>{s.user?.email ?? <span style={{ color: "var(--ghost)" }}>Anonymous</span>}</td>
                  <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{new Date(s.lastSeenAt).toLocaleTimeString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
